import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// Hard ceiling for the add-to-cart request. The buttons are disabled for the
// duration of the request, and #submitForm returns early while any of them is
// disabled — so a request that never settles would lock add-to-cart until the
// page is reloaded. Aborting guarantees the .finally() below always runs.
const ADD_TO_CART_REQUEST_TIMEOUT = 15000;

/**
 * Single in-flight /cart.js request shared by every product-form-component.
 * A cart:update fans out to all of them and each used to fire its own fetch —
 * one add-to-cart on a page with several forms produced 3-4 identical
 * requests.
 * @type {Promise<any> | null}
 */
let inflightCartFetch = null;

/** @returns {Promise<any>} The parsed /cart.js payload */
function fetchCartShared() {
  if (!inflightCartFetch) {
    inflightCartFetch = fetch('/cart.js')
      .then((response) => response.json())
      .finally(() => {
        inflightCartFetch = null;
      });
  }
  return inflightCartFetch;
}

/**
 * Set on <body> from the moment an add that will ask the prescription question is sent until the
 * popup opens, or until the add turns out not to need it. Rebuy fires its bundle offer as soon as it
 * sees the add — before the cart refetch below decides whether to ask — so this is what keeps that
 * offer out of sight in between (assets/rx-choice-modal.css, assets/sl-bundle-upsell-close.js).
 */
export const RX_CHOICE_PENDING_ATTRIBUTE = 'data-rx-modal-pending';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */(timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    try {
      const form = this.closest('form');
      if (!form?.checkValidity()) return;

      // Check if adding would exceed max before animating
      const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
      const quantitySelector = productForm?.refs.quantitySelector;
      if (quantitySelector?.canAddToCart) {
        const validation = quantitySelector.canAddToCart();
        // Don't animate if it would exceed max
        if (!validation.canAdd) {
          return;
        }
      }
      if (this.refs.addToCartButton.dataset.puppet !== 'true') {
        const animationEnabled = this.dataset.addToCartAnimation === 'true';
        if (animationEnabled && !event.target.closest('.quick-add-modal')) {
          this.#animateFlyToCart();
        }
        this.animateAddToCart();
      }
    } catch (error) {
      console.error('Add to cart click handler failed:', error);
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Undoes the optimistic "added" state, for an add that turned out to fail.
   *
   * The animation is started from the click handler so the button responds instantly, which means
   * it is showing success before anything is known. Nothing used to take it back, so a failed add
   * looked exactly like a successful one.
   */
  cancelAddedState() {
    const { addToCartButton } = this.refs;
    if (!addToCartButton) return;

    this.#resetTimeouts?.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];
    addToCartButton.removeAttribute('data-added');
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */(timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const cart = await fetchCartShared();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    // Stop default behaviour from the browser
    event.preventDefault();

    try {
      this.#submitForm(event);
    } catch (error) {
      console.error('Add to cart submit handler failed:', error);
    }
  }

  /**
   * Builds the cart payload from the form, runs quantity validation, and sends
   * the add-to-cart request. Split out of handleSubmit so the synchronous
   * validation logic (which can throw) shares one try/catch with the async chain.
   * @param {Event} event - The submit event.
   */
  #submitForm(event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');
    // Custom upsell check
    const isUpsellVariant = this.dataset?.isUpsell;
    const rxModal = this.#rxModal(isUpsellVariant);
    // The upsell branch below disables the buttons deliberately and they must stay
    // that way — the .finally() re-enable at the end of the request would otherwise
    // undo it and hand the shopper a second add-to-cart inside the open modal.
    let upsellLockedButtons = false;
    // Set once the POST has come back clean. Everything after that point — the cart
    // refetch, the RX upsell modal, the CartAddEvent — runs inside the same promise
    // chain, so a failure there also lands in the .catch below. Without this flag the
    // shopper is told "Error adding to cart" for an item that WAS added.
    let addSucceeded = false;
    if (isUpsellVariant && rxModal) {
      if (rxModal.querySelector('dialog[open]')) {
        // Disable allAddToCartContainers buttons
        for (const container of allAddToCartContainers) {
          container.refs.addToCartButton?.setAttribute('disabled', 'true');
        }
        upsellLockedButtons = true;
        // rxModal.closeDialog();
      }
    }
    // End of custom upsell check

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    // Disable all add-to-cart buttons for the duration of the request so a
    // double click (or spam click) can't fire a second /cart.js request
    // before the first one resolves.
    for (const container of allAddToCartContainers) {
      container.disable();
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    // This add may ask the prescription question: hold any Rebuy popup back until it is settled.
    const rxChoicePending = Boolean(this.dataset.rxChoice && rxModal);
    let rxChoiceShown = false;
    if (rxChoicePending) document.body.setAttribute(RX_CHOICE_PENDING_ATTRIBUTE, '');

    const fetchCfg = fetchConfig('javascript', { body: formData });
    const timeoutController = new AbortController();
    const requestTimeout = setTimeout(() => timeoutController.abort(), ADD_TO_CART_REQUEST_TIMEOUT);

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      signal: timeoutController.signal,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              // Keeps the cart drawer from opening over the RX modal and hiding its error message.
              fromRxModal: Boolean(isUpsellVariant && rxModal),
            })
          );

          return;
        } else {
          addSucceeded = true;
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // The extra /cart.js roundtrip is only needed BEFORE the CartAddEvent
          // when the RX upsell decision depends on the cart contents. Otherwise
          // it runs in the background so the cart drawer opens sooner.
          const hasRxUpsell = this.dataset.rxupsell !== undefined && !!rxModal;
          let cart = null;
          if (hasRxUpsell) {
            cart = await this.#fetchAndUpdateCartQuantity();
          } else {
            this.#fetchAndUpdateCartQuantity();
          }

          // Everything from here to the CartAddEvent is presentation for the RX upsell.
          // It used to sit bare in the promise chain, so a throw here skipped the
          // dispatch below and the cart drawer never opened on an item that HAD been
          // added — the shopper saw nothing happen. Losing the upsell is the acceptable
          // failure; losing the cart drawer is not.
          let shownUpsell = false;
          try {
            // If this submit came from within the open RX upsell modal, close it and
            // re-enable its buttons. The inline script in blocks/rx-popup.liquid does this
            // on product pages, but never runs when the modal arrives via the quick-add
            // fetch (injected scripts don't execute).
            if (isUpsellVariant && rxModal?.querySelector('dialog[open]')) {
              for (const container of allAddToCartContainers) {
                container.refs.addToCartButton?.removeAttribute('disabled');
              }
              /** @type {any} */ (rxModal).closeDialog();
            }

            if (hasRxUpsell) {
              // One id for the legacy modal; the prescription popup lists every service it offers,
              // and any of them already in the cart means the shopper has settled how they get the Rx.
              // That is the ONLY reason to skip it: "I have a valid Rx" adds nothing, so an Rx product
              // added to a cart without a service always asks again — after an emptied cart included.
              const upsellProductIds = (rxModal.dataset.upsellProductId ?? '').split(',');
              const isUpsellInCart = cart?.items?.some((item) => upsellProductIds.includes(item.product_id.toString()));
              if (!isUpsellInCart) {
                shownUpsell = true;
                await this.#showRxModal(rxModal);
                // From here the popup itself clears the pending mark, when it opens.
                rxChoiceShown = rxChoicePending;
              }
            }
          } catch (error) {
            shownUpsell = false;
            console.error('RX upsell step failed after a successful add to cart:', error);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
              upsellShown: shownUpsell,
              // An add made from inside an RX modal: the modal's close opens the cart drawer
              // (assets/cart-drawer.js → #handleRxModalClose), or leaves it to a Rebuy popup that is
              // still waiting (assets/sl-bundle-upsell-close.js). Nothing else should open it.
              fromRxModal: Boolean(isUpsellVariant && rxModal),
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
        // Only claim the add failed when it actually did. A throw from the post-add UI
        // work (cart refetch, RX upsell modal, section morph) means the item IS in the
        // cart, and telling the shopper otherwise sends them to add it twice.
        if (!addSucceeded) {
          // Take the checkmark back FIRST. `animateAddToCart` runs in the click handler, before the
          // request is even sent, so a failed add still plays the success animation: recorded on the
          // preview, a `/cart/add.js` that answered 429 left the card showing a tick for a product
          // that never entered the cart, and no popup, which reads as "the modal is broken".
          for (const container of allAddToCartContainers) container.cancelAddedState?.();

          // A network failure used to leave the shopper with a button that visibly did
          // nothing. Say so instead — a silent failure is indistinguishable from a dead click.
          this.#showAddToCartError(Theme.translations?.add_to_cart_error);
        }
      })
      .finally(() => {
        clearTimeout(requestTimeout);
        // No popup after all (failed add, service already in the cart, already answered): let the
        // Rebuy popup that was held back for it appear.
        if (rxChoicePending && !rxChoiceShown) document.body.removeAttribute(RX_CHOICE_PENDING_ATTRIBUTE);
        // The upsell lock only holds for an add that landed (the modal is closing). A failed add must
        // hand the button back, or the modal is left with a dead button and no way to retry.
        if (!upsellLockedButtons || !addSucceeded) {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * The RX modal this add deals with. An add made from INSIDE one is answering it. Any other add
   * opens the modal its form was marked for: this product's prescription popup when the form carries
   * `data-rx-choice` (snippets/rx-choice-modal.liquid, whose id ends in the product id), otherwise the
   * legacy `#rx-modal`. None for a variant sold without a prescription (`rxExempt`): that add goes
   * the way any other add does.
   * @param {string | undefined} isUpsellVariant - `data-is-upsell` of this form
   * @returns {HTMLElement | null}
   */
  #rxModal(isUpsellVariant) {
    if (isUpsellVariant) return this.closest('[data-rx-modal]') ?? document.getElementById('rx-modal');
    if (this.dataset.rxChoice) {
      return this.rxExempt ? null : document.getElementById(`rx-choice-${this.dataset.productId}`);
    }
    return document.getElementById('rx-modal');
  }

  /**
   * The variant this form is about to add is sold without a prescription: its RX metafield
   * (`custom.rx`) says "No", like the frame-only kit on a mask's page. blocks/buy-buttons.liquid lists
   * those variants in `data-rx-exempt-variants`. Read at submit time, so it follows the selected
   * variant; assets/cart-drawer.js reads it too, to open the drawer on submit as for any other add.
   * @returns {boolean}
   */
  get rxExempt() {
    const variantId = this.refs.variantId?.value;
    const exemptVariants = this.dataset.rxExemptVariants;
    if (!variantId || !exemptVariants) return false;
    return exemptVariants.split(',').includes(variantId);
  }

  /**
   * Shows the RX upsell modal. quick-add.js re-parents every injected modal to
   * <body> before morphing content in (#extractNestedModals), so the modal is
   * normally never nested inside the quick-add dialog — the re-parent here is a
   * defensive fallback. Showing is deferred while the quick-add dialog is open:
   * its close would otherwise strip the scroll-lock attribute (a plain toggle
   * in dialog.js, not a counter) that this modal just set.
   * @param {HTMLElement} rxModal - The RX modal dialog component.
   */
  async #showRxModal(rxModal) {
    // The prescription popup's own <script> never runs when the popup arrives inside a quick-add
    // fetch (injected scripts do not execute), so its component is loaded here before it is shown.
    if (rxModal.hasAttribute('data-rx-choice-modal') && !customElements.get('rx-choice-component')) {
      await import('@theme/rx-choice');
    }

    if (rxModal.closest('quick-add-dialog')) {
      // Drop any stale copy left behind by a previous quick-add flow before re-parenting.
      for (const stale of document.querySelectorAll(`body > [id="${rxModal.id}"]`)) {
        stale.remove();
      }
      document.body.appendChild(rxModal);
    }

    const quickAddDialog = document.getElementById('quick-add-dialog');
    const quickAddDialogElement = quickAddDialog?.querySelector('dialog');
    if (quickAddDialog && quickAddDialogElement?.open) {
      quickAddDialog.addEventListener(DialogCloseEvent.eventName, () => /** @type {any} */ (rxModal).showDialog(), {
        once: true,
      });
    } else {
      /** @type {any} */ (rxModal).showDialog();
    }
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  /**
   * Surfaces an add-to-cart failure in the UI and to screen readers, using the same
   * slot the server-side error path uses. Without this the shopper sees nothing at
   * all when the request fails, which is exactly what a dead click looks like.
   * @param {string} [message] - Message to show; falls back to the stock string.
   */
  #showAddToCartError(message) {
    const { addToCartTextError } = this.refs;
    const text = message || 'Error adding to cart';
    this.#setLiveRegionText(text);

    if (!addToCartTextError) return;
    addToCartTextError.classList.remove('hidden');

    // Reuse the text node the markup already carries, like the server-error branch does
    const textNode = addToCartTextError.childNodes[2];
    if (textNode) {
      textNode.textContent = text;
    } else {
      addToCartTextError.appendChild(document.createTextNode(text));
    }

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      addToCartTextError.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */(newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';
    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
